import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Activity, Plug, Settings2, ListChecks, LogOut as ExitIcon,
  LineChart, History, Radio, ShieldAlert, AlertTriangle, FlaskConical, GitCompare, Layers, Users, LogOut, ChevronDown, ChevronRight, Zap, Library, BarChart3, FileDown,
} from "lucide-react";
import { api, fmtUsd } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { countEffectiveConditions, summarizeGroups } from "@/lib/profileSummary";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";

const NAV = [
  { section: "OVERVIEW" },
  { to: "/", label: "Dashboard", icon: Activity, end: true, testid: "nav-dashboard" },
  { to: "/exchange", label: "Exchange Connection", icon: Plug, testid: "nav-exchange" },

  { section: "STRATEGY" },
  { to: "/strategies", label: "Strategy Profiles", icon: Library, testid: "nav-strategies" },
  { to: "/strategy-slots", label: "Strategy Slots", icon: Library, testid: "nav-strategy-slots" },
  { to: "/entry-rules", label: "Entry Rules", icon: ListChecks, testid: "nav-entry-rules" },
  { to: "/exit-rules", label: "Exit Rules", icon: ExitIcon, testid: "nav-exit-rules" },
  { to: "/validation", label: "Signal Validation", icon: Zap, testid: "nav-validation", highlight: true },
  { to: "/signals", label: "Signal Logs", icon: Radio, testid: "nav-signals" },

  { section: "POSITIONS" },
  { to: "/positions", label: "Open Positions", icon: LineChart, testid: "nav-positions" },
  { to: "/history", label: "Trade History", icon: History, testid: "nav-history" },
  { to: "/strategy-trades", label: "Strategy Performance", icon: BarChart3, testid: "nav-strategy-trades" },

  { section: "ANALYSIS" },
  { group: "Strategy Analysis", icon: FlaskConical, testid: "nav-group-analysis", items: [
    { to: "/backtest", label: "Backtest", icon: FlaskConical, testid: "nav-backtest" },
    { to: "/compare", label: "Compare Backtests", icon: GitCompare, testid: "nav-compare" },
    { to: "/walkforward", label: "Walk-Forward", icon: Layers, testid: "nav-walkforward" },
    { to: "/backtest-v2", label: "Institutional Reports", icon: FileDown, testid: "nav-institutional-reports" },
  ]},

  { section: "CONFIG" },
  { to: "/settings", label: "Bot Settings", icon: Settings2, testid: "nav-settings" },
  { to: "/risk", label: "Risk Settings", icon: ShieldAlert, testid: "nav-risk" },
];

const ANALYSIS_PATHS = ["/backtest", "/compare", "/walkforward", "/backtest-v2"];

export default function Layout() {
  const [status, setStatus] = useState(null);
  const [phase, setPhase] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, isAdmin, signOut, signingOut, authRequired } = useAuth();

  // Strategy Analysis group: open by default when on one of its routes,
  // persist user preference in localStorage across navigation.
  const [analysisOpen, setAnalysisOpen] = useState(() => {
    try {
      const saved = window.localStorage.getItem("ui.analysisOpen");
      if (saved !== null) return saved === "1";
    } catch {}
    return ANALYSIS_PATHS.some((p) => location.pathname.startsWith(p));
  });
  useEffect(() => {
    try { window.localStorage.setItem("ui.analysisOpen", analysisOpen ? "1" : "0"); } catch {}
  }, [analysisOpen]);
  // Auto-open when user navigates INTO an analysis route (but don't auto-close).
  useEffect(() => {
    if (ANALYSIS_PATHS.some((p) => location.pathname.startsWith(p))) setAnalysisOpen(true);
  }, [location.pathname]);

  const onLogout = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    try {
      await signOut();
    } catch (err) {
      console.error("[auth] signOut threw", err);
    }
    // Hard navigation guarantees no React state, no axios interceptor,
    // and no service-worker cache carry over.
    window.location.replace("/login");
  };

  useEffect(() => {
    let mounted = true;
    const tick = async () => {
      try {
        const s = await api.status();
        if (mounted) setStatus(s);
      } catch {}
      try {
        const ph = await api.validationPhaseStatus();
        if (mounted) setPhase(ph);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 4000);
    // Listen for in-page mutations (BotSettings save, etc.) so the
    // banner + sidebar balance refresh on the same axios cycle as the
    // PUT — without this the operator sees the old bot_mode for up to
    // 4s, which previously made it look like the save failed.
    const onSaved = () => { tick(); };
    window.addEventListener("bot-settings:saved", onSaved);
    return () => {
      mounted = false;
      clearInterval(id);
      window.removeEventListener("bot-settings:saved", onSaved);
    };
  }, [location.pathname]);

  const mode = status?.bot_settings?.bot_mode || "paper";
  const isPaper = mode === "paper" || mode === "testnet" || mode === "live_disabled";
  const liveEnvOn = status?.live_trading_enabled_in_env;
  const phaseOn = !!status?.bot_settings?.validation_phase_enabled;
  const runtimeRunning = status?.runtime?.state === "running";
  const autoExecActive = phaseOn && runtimeRunning && (mode === "testnet" || mode === "live_enabled" || mode === "lbank_live_enabled");

  // Truthful execution label. The previous version hardcoded "TESTNET"
  // even on live modes, which made it impossible to tell at a glance
  // whether the engine was about to route real orders.
  const EXEC_LABEL = {
    paper: "PAPER",
    testnet: "TESTNET",
    live_disabled: "EXECUTION DISABLED",
    live_enabled: "BINANCE LIVE",
    lbank_live_enabled: "LBANK LIVE",
  };
  const execLabel = EXEC_LABEL[mode] || String(mode).toUpperCase();
  const isLiveExec = mode === "live_enabled" || mode === "lbank_live_enabled";

  // Connection pill — separate axis from bot_mode. Successfully testing
  // Binance Live does NOT flip bot_mode, and the UI must make that
  // separation obvious so the operator never mistakes a green
  // connection for active live execution.
  const conn = status?.exchange_connection || {};
  const connExchange = conn.exchange || "paper";
  const connStatus = conn.status || "paper_mode";
  const connOk = connStatus === "connected";
  const CONN_LABEL = {
    paper: "PAPER",
    binance_testnet: "BINANCE TESTNET",
    binance_live: "BINANCE LIVE",
    lbank: "LBANK",
  };
  const connLabel = CONN_LABEL[connExchange] || connExchange.toUpperCase();

  // Sidebar balance — source-of-truth per bot_mode. The old UI always
  // rendered `paper_balance_usdt` even when the engine was routing to
  // testnet or live, making the operator believe $10k was their live
  // money. This resolver picks the right source, surfaces a clear
  // "N/A — Test connection" when the last-tested exchange doesn't
  // match the current mode, and labels the line explicitly so the
  // operator can never confuse paper seed with exchange balance.
  const resolveBalance = () => {
    if (mode === "paper" || mode === "live_disabled") {
      return {
        label: mode === "paper" ? "Paper balance (sim)" : "Execution disabled",
        value: Number(status?.paper_balance_usdt || 0),
        valueType: "number",
        tone: "neutral",
      };
    }
    const expectedExchange =
      mode === "testnet" ? "binance_testnet"
      : mode === "live_enabled" ? "binance_live"
      : mode === "lbank_live_enabled" ? "lbank"
      : null;
    const label =
      mode === "testnet" ? "Testnet balance"
      : mode === "live_enabled" ? "Live balance (Binance)"
      : mode === "lbank_live_enabled" ? "Live balance (LBank)"
      : "Balance";
    const tested = conn.last_tested_exchange;
    const wallet = conn.last_total_wallet_balance;
    const matches = tested && tested === expectedExchange;
    if (!matches || wallet === null || wallet === undefined) {
      return { label, value: "Not refreshed", valueType: "text", tone: "muted" };
    }
    return {
      label, value: Number(wallet), valueType: "number",
      tone: isLiveExec ? "live" : "neutral",
    };
  };
  const balanceInfo = resolveBalance();

  return (
    <div className="App min-h-screen flex flex-col bg-[#05050A] text-white">
      {/* Global top warning banner */}
      {phase?.active_strategy_missing ? (
        <div
          data-testid="missing-strategy-banner"
          className="flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wider border-b bg-[#FF3B30]/10 border-[#FF3B30]/40 text-[#FF3B30]"
        >
          <AlertTriangle size={14} strokeWidth={2} />
          <span className="font-semibold">⚠ Active strategy was deleted</span>
          <span className="text-[#FF3B30]/80 normal-case tracking-normal">
            · ID {(phase.active_strategy_missing_id || "").slice(0, 8)}… is no longer evaluated. Engine is idle. Activate a profile in
          </span>
          <a href="/strategies" className="text-[#FF3B30] underline decoration-dotted hover:decoration-solid normal-case tracking-normal">Strategy Profiles →</a>
        </div>
      ) : null}
      <div
        data-testid="global-mode-banner"
        data-mode={mode}
        data-connection={connExchange}
        className={`flex items-center gap-3 px-4 py-2 text-xs uppercase tracking-wider border-b ${
          autoExecActive
            ? "bg-[#00E676]/10 border-[#00E676]/40 text-[#00E676]"
            : isPaper
              ? "bg-[#F59E0B]/10 border-[#F59E0B]/30 text-[#F59E0B]"
              : "bg-[#FF3B30]/10 border-[#FF3B30]/30 text-[#FF3B30]"
        }`}
      >
        {autoExecActive ? (
          <>
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00E676] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00E676]" />
            </span>
            <span className="font-semibold" data-testid="auto-exec-banner-label">
              AUTO EXECUTION ACTIVE · {execLabel}
            </span>
            <span className="text-[#A0A0AB] normal-case tracking-normal" data-testid="auto-exec-banner-detail">
              {(() => {
                const p = phase?.active_profile;
                const tf = phase?.timeframe || p?.timeframe || "—";
                const cd = phase?.cooldown_minutes || p?.cooldown_minutes;
                const name = p?.name || "no active profile (hardcoded RSI)";
                // Composite profiles store conditions inside
                // `condition_groups[*].conditions` while leaving the
                // flat `conditions` array empty. Counting only the
                // flat list made fully-configured composites render as
                // "0 conditions" in the live banner. The shared helper
                // mirrors the engine's evaluator so a future fourth UI
                // surface can never re-introduce this bug.
                const totalConds = countEffectiveConditions(p);
                const groups = summarizeGroups(p);
                const combinator = p?.combinator || "AND";
                let condSummary;
                if (!p) {
                  condSummary = "RSI divergence";
                } else if (groups.length > 0) {
                  condSummary = `${groups.length} groups · ${totalConds} conds`;
                } else if (totalConds > 0) {
                  condSummary = `${totalConds} cond · ${combinator}`;
                } else {
                  condSummary = "0 conditions";
                }
                const exitMode = p?.exit_rules?.mode || "tp_sl";
                const s = phase?.sizing || {};
                const lev = s.effective_leverage ?? phase?.leverage ?? 1;
                const not_ = s.effective_notional ?? phase?.notional_usdt ?? 0;
                const maxL = phase?.max_open_long_positions ?? 1;
                const maxS = phase?.max_open_short_positions ?? 1;
                return `· BTCUSDT · ${tf} · ${name} · ${condSummary} · exit: ${exitMode} · ${Number(not_).toFixed(0)} USDT · ${lev}× · max ${maxL}L/${maxS}S pos · ${cd ?? 5}-min cooldown`;
              })()}
            </span>
          </>
        ) : mode === "live_disabled" && connExchange === "binance_live" && connOk ? (
          <>
            <AlertTriangle size={14} strokeWidth={1.5} />
            <span className="font-semibold" data-testid="banner-live-connected-but-not-live">
              BINANCE LIVE CONNECTED · EXECUTION NOT LIVE
            </span>
            <span className="text-[#71717A] normal-case tracking-normal">
              · change bot mode in Bot Settings to route orders live
            </span>
          </>
        ) : mode === "live_disabled" && connExchange === "lbank" && connOk ? (
          <>
            <AlertTriangle size={14} strokeWidth={1.5} />
            <span className="font-semibold" data-testid="banner-lbank-connected-but-not-live">
              LBANK CONNECTED · EXECUTION NOT LIVE
            </span>
            <span className="text-[#71717A] normal-case tracking-normal">
              · change bot mode in Bot Settings to route orders live
            </span>
          </>
        ) : (
          <>
            <AlertTriangle size={14} strokeWidth={1.5} />
            <span className="font-semibold" data-testid="banner-execution-label">
              EXECUTION: {execLabel}
            </span>
            <span className="text-[#71717A] normal-case tracking-normal">
              · connection: {connLabel}{connOk ? " (connected)" : ""} · server live trading: {liveEnvOn ? "ENABLED" : "DISABLED (LIVE_TRADING_ENABLED=false)"}
            </span>
          </>
        )}
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className="w-60 shrink-0 border-r border-border bg-[#0F0F14] flex flex-col">
          <div className="px-4 py-4 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#00E676] pulse-dot" />
              <span className="font-semibold tracking-tight text-sm">BTC TRADING BOT</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#71717A] mt-1">
              Money Printer v1
            </div>
          </div>
          <nav className="flex-1 py-2 overflow-y-auto">
            {NAV.map((n, idx) => {
              // Section header (visual divider + uppercase label)
              if (n.section) {
                return (
                  <div
                    key={`section-${n.section}-${idx}`}
                    data-testid={`nav-section-${n.section.toLowerCase()}`}
                    className={`px-4 pt-4 pb-1 text-[9px] uppercase tracking-[0.18em] text-[#52525B] font-semibold ${idx === 0 ? "pt-2" : ""}`}
                  >
                    {n.section}
                  </div>
                );
              }
              // Grouped section with toggleable children
              if (n.group) {
                const anyChildActive = n.items.some((c) => location.pathname.startsWith(c.to));
                const GroupIcon = n.icon;
                return (
                  <div key={n.group} data-testid={n.testid}>
                    <button
                      type="button"
                      onClick={() => setAnalysisOpen((v) => !v)}
                      aria-expanded={analysisOpen}
                      data-testid={`${n.testid}-toggle`}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                        anyChildActive && !analysisOpen
                          ? "border-[#00E676]/40 text-white"
                          : "border-transparent text-[#A0A0AB] hover:text-white hover:bg-[#1A1A22]"
                      }`}
                    >
                      <GroupIcon size={16} strokeWidth={1.5} />
                      <span className="flex-1 text-left">{n.group}</span>
                      {analysisOpen
                        ? <ChevronDown size={14} strokeWidth={1.5} className="text-[#71717A]" />
                        : <ChevronRight size={14} strokeWidth={1.5} className="text-[#71717A]" />}
                    </button>
                    {analysisOpen ? (
                      <div className="py-0.5 bg-[#0A0A10]/40">
                        {n.items.map((c) => {
                          const ChildIcon = c.icon;
                          return (
                            <NavLink
                              key={c.to}
                              to={c.to}
                              data-testid={c.testid}
                              className={({ isActive }) =>
                                `flex items-center gap-3 pl-10 pr-4 py-2 text-[13px] border-l-2 transition-colors ${
                                  isActive
                                    ? "border-[#00E676] bg-[#1A1A22] text-white"
                                    : "border-transparent text-[#A0A0AB] hover:text-white hover:bg-[#1A1A22]"
                                }`
                              }
                            >
                              <ChildIcon size={14} strokeWidth={1.5} />
                              <span>{c.label}</span>
                            </NavLink>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              }
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                      isActive
                        ? "border-[#00E676] bg-[#1A1A22] text-white"
                        : n.highlight
                          ? "border-transparent text-[#00E676]/90 hover:text-white hover:bg-[#1A1A22]"
                          : "border-transparent text-[#A0A0AB] hover:text-white hover:bg-[#1A1A22]"
                    }`
                  }
                >
                  <Icon size={16} strokeWidth={1.5} />
                  <span>{n.label}</span>
                </NavLink>
              );
            })}
            {isAdmin ? (
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors ${
                    isActive
                      ? "border-[#F59E0B] bg-[#1A1A22] text-white"
                      : "border-transparent text-[#A0A0AB] hover:text-white hover:bg-[#1A1A22]"
                  }`
                }
              >
                <Users size={16} strokeWidth={1.5} />
                <span>Admin Panel</span>
              </NavLink>
            ) : null}
          </nav>
          <div className="px-4 py-3 border-t border-border text-[10px] uppercase tracking-widest text-[#71717A]">
            {profile && !profile.is_anonymous ? (
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono normal-case text-[#A0A0AB] truncate" data-testid="sidebar-user-email" title={profile.email}>
                    {profile.email}
                  </div>
                  <div className="text-[#71717A] mt-0.5">role: <span className="text-white">{profile.role}</span></div>
                </div>
                <button
                  data-testid="logout-btn"
                  type="button"
                  onClick={onLogout}
                  disabled={signingOut}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-border bg-[#1A1A22] hover:bg-[#262633] text-white normal-case tracking-normal text-xs disabled:opacity-50 disabled:cursor-wait"
                  title="Sign out"
                >
                  {signingOut ? (
                    <>
                      <span className="inline-block w-3 h-3 border-2 border-[#71717A] border-t-white rounded-full animate-spin" />
                      <span>Signing out…</span>
                    </>
                  ) : (
                    <>
                      <LogOut size={12} strokeWidth={1.5} /> Sign out
                    </>
                  )}
                </button>
              </div>
            ) : authRequired ? null : (
              <div className="mt-2 text-[#71717A] normal-case tracking-normal">auth disabled</div>
            )}
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          {/* Route-level error boundary. Catches ChunkLoadError on
              stale builds and any render-time exception inside the
              page. Keeps sidebar visible; never silently unmounts. */}
          <RouteErrorBoundary>
            <Outlet context={{ status }} />
          </RouteErrorBoundary>
        </main>
      </div>
    </div>
  );
}
